const OPEN_ORDER_SESSION_STORAGE_KEY = 'roomnexa:pos-open-order:v1';

const roundCurrency = (value) => Math.round(Number(value || 0) * 100) / 100;

export const formatCurrency = (value, currency = 'INR') => {
  const amount = Number(value || 0);
  return `${currency} ${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const buildCartKey = (menuItemId, variationId = null) => `${menuItemId}:${variationId || 'base'}`;

export const getOpenOrderContextKey = ({ hotelId, targetType, targetId, qrToken }) => (
  [hotelId || '', targetType || '', targetId || '', qrToken || ''].join(':')
);

const readSessionStore = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(OPEN_ORDER_SESSION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeSessionStore = (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(OPEN_ORDER_SESSION_STORAGE_KEY, JSON.stringify(value));
  } catch (_) {
    // Ignore storage write failures.
  }
};

/** Guest QR flow: preparation time (minutes) for KOT — matches backend 5–240. */
export const PREP_TIME_MIN = 5;
export const PREP_TIME_MAX = 240;
export const PREP_PRESET_MINUTES = [15, 30, 45];

export function sessionMinutesToPrepUiState(minutes) {
  const m = Number(minutes ?? 30);
  const c = Number.isFinite(m)
    ? Math.min(PREP_TIME_MAX, Math.max(PREP_TIME_MIN, Math.round(m)))
    : 30;
  if (PREP_PRESET_MINUTES.includes(c)) {
    return { prepChoice: String(c), prepCustom: String(c) };
  }
  return { prepChoice: 'custom', prepCustom: String(c) };
}

export function resolvePrepTimeMinutes(prepChoice, prepCustom) {
  if (prepChoice === 'custom') {
    const n = parseInt(String(prepCustom ?? '').replace(/\D/g, ''), 10);
    return Number.isFinite(n)
      ? Math.min(PREP_TIME_MAX, Math.max(PREP_TIME_MIN, n))
      : 30;
  }
  const n = Number(prepChoice);
  return Number.isFinite(n)
    ? Math.min(PREP_TIME_MAX, Math.max(PREP_TIME_MIN, n))
    : 30;
}

export const normalizeCartMap = (rawCart) => {
  if (!rawCart || typeof rawCart !== 'object' || Array.isArray(rawCart)) return {};
  return Object.entries(rawCart).reduce((acc, [key, row]) => {
    if (!row || typeof row !== 'object') return acc;
    const quantity = Math.max(0, Number(row.quantity || 0));
    if (!quantity) return acc;
    acc[key] = {
      key: String(row.key || key),
      menuItemId: Number(row.menuItemId || row.menu_item || 0),
      name: String(row.name || row.item_name || '').trim(),
      variationId: row.variationId ?? row.variation_id ?? null,
      variationLabel: String(row.variationLabel || row.variation_name || '').trim(),
      unitPrice: roundCurrency(row.unitPrice ?? row.unit_price ?? 0),
      quantity,
    };
    return acc;
  }, {});
};

export const getOpenOrderSession = (queryParams) => {
  const store = readSessionStore();
  const contextKey = getOpenOrderContextKey(queryParams);
  const row = store[contextKey];
  if (!row || typeof row !== 'object') {
    return {
      cart: {},
      customerForm: {
        customer_name: '',
        customer_phone_country_code: '+91',
        customer_phone: '',
        notes: '',
      },
      preparationTimeMinutes: 30,
    };
  }
  const customerForm = row.customerForm && typeof row.customerForm === 'object'
    ? row.customerForm
    : {};
  const rawPrep = Number(row.preparationTimeMinutes ?? 30);
  const preparationTimeMinutes = Number.isFinite(rawPrep)
    ? Math.min(240, Math.max(5, Math.round(rawPrep)))
    : 30;
  return {
    cart: normalizeCartMap(row.cart),
    customerForm: {
      customer_name: String(customerForm.customer_name || '').trimStart(),
      customer_phone_country_code: String(customerForm.customer_phone_country_code || '+91').trim() || '+91',
      customer_phone: String(customerForm.customer_phone || '').replace(/\D/g, '').slice(0, 10),
      notes: String(customerForm.notes || '').trimStart(),
    },
    preparationTimeMinutes,
  };
};

export const updateOpenOrderSession = (queryParams, patch) => {
  const contextKey = getOpenOrderContextKey(queryParams);
  const store = readSessionStore();
  const current = getOpenOrderSession(queryParams);
  const next = {
    ...current,
    ...(patch || {}),
  };
  if (patch?.cart) {
    next.cart = normalizeCartMap(patch.cart);
  }
  if (patch?.customerForm) {
    next.customerForm = {
      ...current.customerForm,
      ...patch.customerForm,
      customer_phone: String(patch.customerForm.customer_phone ?? current.customerForm.customer_phone ?? '')
        .replace(/\D/g, '')
        .slice(0, 10),
    };
  }
  if (patch?.preparationTimeMinutes != null) {
    const p = Number(patch.preparationTimeMinutes);
    next.preparationTimeMinutes = Number.isFinite(p)
      ? Math.min(240, Math.max(5, Math.round(p)))
      : current.preparationTimeMinutes;
  }
  store[contextKey] = next;
  writeSessionStore(store);
};

export const clearOpenOrderSession = (queryParams) => {
  const contextKey = getOpenOrderContextKey(queryParams);
  const store = readSessionStore();
  delete store[contextKey];
  writeSessionStore(store);
};

export const getItemVariationOptions = (item, variationLabelMap = new Map()) => {
  const rows = Array.isArray(item?.variation_config) ? item.variation_config : [];
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const rawVariationId = row.variation_id;
      const variationId = rawVariationId === null || rawVariationId === undefined || rawVariationId === ''
        ? null
        : Number(rawVariationId);
      const variationLabel = String(
        row.variation_name
        || row.name
        || variationLabelMap.get(Number(rawVariationId))
        || ''
      ).trim();
      return {
        variationId,
        variationLabel,
        unitPrice: roundCurrency(row.price || 0),
      };
    })
    .filter(Boolean);
};

export const getResolvedItemVariation = (item, selectedVariationId, variationLabelMap = new Map()) => {
  const variationOptions = getItemVariationOptions(item, variationLabelMap);
  if (!variationOptions.length) {
    return {
      variationId: null,
      variationLabel: '',
      unitPrice: roundCurrency(item?.price || 0),
    };
  }
  const matchedOption = variationOptions.find(
    (option) => String(option.variationId) === String(selectedVariationId)
  );
  return matchedOption || variationOptions[0];
};

export const getCartRows = (cartMap) => Object.values(normalizeCartMap(cartMap));

export const getCartCount = (cartMap) => getCartRows(cartMap).reduce(
  (sum, row) => sum + Number(row.quantity || 0),
  0
);

export const getCartSubtotal = (cartMap) => roundCurrency(
  getCartRows(cartMap).reduce(
    (sum, row) => sum + Number(row.unitPrice || 0) * Number(row.quantity || 0),
    0
  )
);

const parseTaxRuleNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const shouldApplyRuleForSubtotal = (rule, subtotal) => {
  const minAmount = parseTaxRuleNumber(rule?.min_amount);
  const maxAmount = parseTaxRuleNumber(rule?.max_amount);
  if (minAmount !== null && subtotal < minAmount) return false;
  if (maxAmount !== null && subtotal > maxAmount) return false;
  return true;
};

const buildTaxSummaryFromRules = (subtotal, taxRules = []) => {
  const normalizedRules = Array.isArray(taxRules) ? taxRules : [];
  let totalTax = 0;
  let totalRate = 0;
  let cgstRate = 0;
  let sgstRate = 0;
  let cgstAmount = 0;
  let sgstAmount = 0;
  let unclassifiedRate = 0;
  let unclassifiedAmount = 0;
  let appliedRules = 0;

  normalizedRules.forEach((rule) => {
    if (!rule || typeof rule !== 'object') return;
    if (!shouldApplyRuleForSubtotal(rule, subtotal)) return;
    const ruleType = String(rule.tax_type || '').trim().toLowerCase();
    const percentage = parseTaxRuleNumber(rule.tax_percentage);
    const fixedAmount = parseTaxRuleNumber(rule.tax_amount);
    const calculatedTax = percentage !== null
      ? (subtotal * percentage) / 100
      : (fixedAmount !== null ? fixedAmount : 0);
    const taxAmount = roundCurrency(calculatedTax);
    if (taxAmount <= 0) return;

    appliedRules += 1;
    totalTax = roundCurrency(totalTax + taxAmount);
    if (percentage !== null) {
      totalRate = roundCurrency(totalRate + percentage);
      if (ruleType === 'cgst') cgstRate = roundCurrency(cgstRate + percentage);
      else if (ruleType === 'sgst') sgstRate = roundCurrency(sgstRate + percentage);
      else unclassifiedRate = roundCurrency(unclassifiedRate + percentage);
    }
    if (ruleType === 'cgst') cgstAmount = roundCurrency(cgstAmount + taxAmount);
    else if (ruleType === 'sgst') sgstAmount = roundCurrency(sgstAmount + taxAmount);
    else unclassifiedAmount = roundCurrency(unclassifiedAmount + taxAmount);
  });

  if (!appliedRules) {
    return null;
  }

  if (unclassifiedRate > 0) {
    const halfRate = roundCurrency(unclassifiedRate / 2);
    cgstRate = roundCurrency(cgstRate + halfRate);
    sgstRate = roundCurrency(sgstRate + (unclassifiedRate - halfRate));
  }
  if (unclassifiedAmount > 0) {
    const halfAmount = roundCurrency(unclassifiedAmount / 2);
    cgstAmount = roundCurrency(cgstAmount + halfAmount);
    sgstAmount = roundCurrency(sgstAmount + (unclassifiedAmount - halfAmount));
  }

  // When both CGST and SGST rules apply, show an equal half-and-half split of total GST
  // (e.g. 5% total → 2.5% + 2.5% with matching amounts), not uneven rule percentages.
  if (totalTax > 0 && totalRate > 0 && cgstRate > 0 && sgstRate > 0) {
    cgstRate = roundCurrency(totalRate / 2);
    sgstRate = roundCurrency(totalRate - cgstRate);
    cgstAmount = roundCurrency(totalTax / 2);
    sgstAmount = roundCurrency(totalTax - cgstAmount);
  }

  // If still one-sided, normalize to an even split for GST display.
  if (totalRate > 0 && ((cgstRate > 0 && sgstRate === 0) || (sgstRate > 0 && cgstRate === 0))) {
    cgstRate = roundCurrency(totalRate / 2);
    sgstRate = roundCurrency(totalRate - cgstRate);
  }
  if (totalTax > 0 && ((cgstAmount > 0 && sgstAmount === 0) || (sgstAmount > 0 && cgstAmount === 0))) {
    cgstAmount = roundCurrency(totalTax / 2);
    sgstAmount = roundCurrency(totalTax - cgstAmount);
  }

  return {
    subtotal,
    gstPercentage: totalRate,
    cgstRate,
    sgstRate,
    cgstAmount,
    sgstAmount,
    totalTax,
    totalPayable: roundCurrency(subtotal + totalTax),
  };
};

export const getOpenOrderBillSummary = (cartMap, settingsOrGst) => {
  const subtotal = getCartSubtotal(cartMap);
  const settings = settingsOrGst && typeof settingsOrGst === 'object'
    ? settingsOrGst
    : {};
  const fromTaxRules = buildTaxSummaryFromRules(subtotal, settings.tax_rules);
  if (fromTaxRules) return fromTaxRules;

  const normalizedGstPercentage = Math.max(
    0,
    Number(settings.gst_percentage ?? settingsOrGst ?? 0)
  );
  const totalTax = roundCurrency(subtotal * normalizedGstPercentage / 100);
  const splitTaxRate = normalizedGstPercentage / 2;
  const cgstAmount = roundCurrency(totalTax / 2);
  const sgstAmount = roundCurrency(totalTax - cgstAmount);
  return {
    subtotal,
    gstPercentage: normalizedGstPercentage,
    cgstRate: splitTaxRate,
    sgstRate: splitTaxRate,
    cgstAmount,
    sgstAmount,
    totalTax,
    totalPayable: roundCurrency(subtotal + totalTax),
  };
};

/**
 * Read percentage from POS order tax_breakdown rows. Backend uses `rate`; client forms use `value` / `numericValue`.
 */
export const parsePosTaxBreakdownPercentage = (row) => {
  const n = Number(
    row?.numericValue ?? row?.value ?? row?.rate ?? row?.tax_percentage ?? row?.percentage ?? 0
  );
  return Number.isFinite(n) ? n : 0;
};

/**
 * Map API/stored tax_breakdown JSON to restaurant billing `applied_taxes` rows (mode + value string).
 */
export const mapPosOrderTaxBreakdownToAppliedTaxRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row, index) => {
    const pct = parsePosTaxBreakdownPercentage(row);
    const fixed = Number(row?.amount ?? row?.tax_amount ?? 0);
    const explicitMode = String(row?.mode || '').toLowerCase();
    const useAmount = explicitMode === 'amount' || (pct <= 0 && fixed > 0);
    const mode = useAmount ? 'amount' : 'percentage';
    const value = mode === 'percentage'
      ? String(Math.max(0, pct))
      : String(Math.max(0, Number.isFinite(fixed) ? fixed : 0));
    return {
      tax_key: row?.tax_key || `loaded-tax-${index}`,
      tax_rule_id: Number(row?.tax_rule_id || 0) || null,
      name: row?.name || row?.tax_type_label || 'Tax',
      tax_type_label: row?.tax_type_label || row?.name || 'Tax',
      mode,
      value,
      enabled: row?.enabled !== false,
    };
  });
};
